import { useState, useEffect } from "react";
import { notificationTempService, type Notification } from "../../services/notificationTemp.service";
import { programService } from "../../services/program.service";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "react-feather";
import { Link } from "react-router-dom";
import { useProgram } from "@/contexts/program";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SpinnerBlock } from "@/components/ui/spinner";
import { usePortalScope } from "@/contexts/portalScope";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Program {
  id: string;
  program: {
    id: string;
  };
  companyName: string;
  name?: string;
}

export function Notifications() {
  let { activePrograms } = useProgram();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const { isMarketplaceHidden } = usePortalScope();
  const [brands, setBrands] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, [currentPage, selectedProgramId]);

  useEffect(() => {
    fetchActiveBrands();
  }, []);
  
  const fetchActiveBrands = async () => {
    try {
      setIsLoadingBrands(true);
      if(activePrograms.length === 0) {
        const response = await programService.findActivePrograms();
        activePrograms = response || [];
      }
      
      // Get the active programs array
      const brandsList = activePrograms.map((program: Program) => ({
        id: program.program?.id || program.id,
        name: program.companyName || program.name || 'Unknown Brand'
      }));
      
      setBrands(brandsList);
    } catch (error) {
      console.error("Failed to fetch active brands:", error);
      toast.error("Failed to load brands");
    } finally {
      setIsLoadingBrands(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      const response = await notificationTempService.getNotifications( {page: currentPage, limit: 10, programId: selectedProgramId} );
      setNotifications(response.data);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBrandSelect = (brandId: string) => {
    setSelectedProgramId(brandId);
    setCurrentPage(1); // Reset to first page when changing brand filter
  };

  const formatNotificationDate = (dateString: string) => {
    return format(new Date(dateString), "dd.MM.yyyy HH:mm");
  };

  const handleActionClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await notificationTempService.markAsRead(notification.id);
        // Update the notification in the local state
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
        );
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight mb-6">Notifications</h1>

      {/* Mark all as read button */}
      <div className="mb-4 flex justify-end">
        <Button
          onClick={async () => {
            try {
              await notificationTempService.markAllAsRead();
              setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
              toast.success("All notifications marked as read");
            } catch {
              toast.error("Failed to mark all as read");
            }
          }}
        >
          Mark all as read
        </Button>
      </div>

      {/* Brand Filter — hidden in a scoped portal, where "All" and the single
          brand are the same set. */}
      <div className={`mb-6${isMarketplaceHidden ? " hidden" : ""}`}>
        <div className="flex items-center mb-2">
          <label className="text-sm font-medium text-muted-foreground mr-2">Brands</label>
        </div>
        <Select
          value={selectedProgramId || "all"}
          onValueChange={(value) => handleBrandSelect(value === "all" ? "" : value)}
          disabled={isLoadingBrands}
        >
          <SelectTrigger className="w-full md:w-64">
            <SelectValue placeholder={isLoadingBrands ? "Loading brands..." : "All"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {brands.map((brand) => (
              <SelectItem key={brand.id} value={brand.id}>
                {brand.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Notifications List */}
      <div className="bg-card rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <SpinnerBlock label="Loading notifications" />
        ) : notifications.length === 0 ? (
          <EmptyState
            bordered={false}
            title="No notifications yet"
            body="Updates from your brands will show up here"
          />
        ) : (
          <div className="divide-y divide-border">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="p-4 border-b last:border-b-0"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3
                        className={`text-sm font-medium ${
                          !notification.read ? "text-foreground" : "text-muted-foreground"
                        }`}
                      >
                        {notification.brandDetail?.companyName || "System"}
                      </h3>
                      {!notification.read && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-800">
                          New
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {notification.message}
                    </p>
                    {notification.suggestion && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {notification.suggestion}
                      </p>
                    )}
                    <div className="mt-2 text-sm text-muted-foreground">
                      {formatNotificationDate(notification.createdAt)}
                    </div>
                    {notification.link && (
                      <div className="mt-2">
                        <Button asChild size="sm">
                          <Link
                            to={notification.link}
                            onClick={() => handleActionClick(notification)}
                          >
                            {notification.link.includes('settings') ? 'Update Settings' : 'View Details'}
                          </Link>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-card px-4 py-3 flex items-center justify-between border-t border-border sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-muted-foreground bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md text-muted-foreground bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Page <span className="font-medium">{currentPage}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Logic to show pages around current page
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-brand-50 border-brand-500 text-brand-600'
                            : 'bg-card border-border text-muted-foreground hover:bg-accent'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
